import React, { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function StepVerify({ data, updateData, onNext, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    sendOtp();
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const sendOtp = async () => {
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`${API_BASE}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: data.phone }),
      });
      const result = await res.json();
      if (result.success) {
        setSent(true);
        setResendTimer(30);
      } else {
        const errMsg = typeof result.error === 'string' && !result.error.startsWith('{')
          ? result.error
          : 'Failed to send code. Please try again.';
        setSendError(errMsg);
      }
    } catch (e) {
      setSendError('Connection failed. Please try again.');
    }
    setSending(false);
  };

  const handleVerify = async () => {
    if (!code || code.length < 4) {
      setError('Enter the verification code sent to your phone');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: data.phone, code }),
      });
      const result = await res.json();
      if (result.success) {
        updateData({ phoneVerified: true });
        onNext();
      } else {
        setError(result.error || 'Invalid code. Please try again.');
        setCode('');
      }
    } catch (e) {
      setError('Connection failed. Please try again.');
    }
    setLoading(false);
  };

  const isUS = data.phone?.startsWith('+1') || data.phone?.startsWith('1');
  const channelLabel = isUS ? 'SMS' : 'WhatsApp';

  return (
    <div className="ob-card">
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 56, height: 56,
          background: 'rgba(26,86,219,0.1)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <MessageSquare size={24} color="var(--blue-light)" />
        </div>
        <h1 className="ob-title">Verify your number</h1>
        {sending ? (
          <p className="ob-subtitle">Sending code via {channelLabel}...</p>
        ) : sendError ? (
          <div>
            <p className="ob-subtitle" style={{
              color: '#ef4444',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              padding: '0 8px',
            }}>{sendError}</p>
            <button
              onClick={sendOtp}
              style={{ background: 'none', border: 'none', color: 'var(--blue-light)', fontSize: 13, cursor: 'pointer', marginTop: 8 }}
            >
              Try again
            </button>
          </div>
        ) : (
          <p className="ob-subtitle">
            We sent a verification code via {channelLabel} to<br />
            <strong style={{ color: 'var(--text-primary)' }}>{data.phone}</strong>
          </p>
        )}
      </div>

      {/* Single code input */}
      <input
        className={`ob-input ${error ? 'error' : ''}`}
        type="text"
        inputMode="numeric"
        placeholder="Enter verification code"
        value={code}
        onChange={e => {
          setCode(e.target.value.replace(/\D/g, ''));
          setError('');
        }}
        onKeyDown={e => e.key === 'Enter' && handleVerify()}
        disabled={sending || !!sendError}
        autoFocus
        style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.15em', fontWeight: 600 }}
      />

      {error && (
        <p className="ob-error" style={{ textAlign: 'center', marginBottom: 12 }}>
          {error}
        </p>
      )}

      <button
        className="ob-btn ob-btn-primary"
        onClick={handleVerify}
        disabled={loading || sending || !!sendError}
        style={{ marginTop: 12 }}
      >
        {loading ? 'Verifying...' : 'Verify & Continue →'}
      </button>

      <div style={{ textAlign: 'center', marginTop: 16 }}>
        {resendTimer > 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Resend code in {resendTimer}s
          </p>
        ) : (
          !sendError && (
            <button
              onClick={sendOtp}
              disabled={sending}
              style={{ background: 'none', border: 'none', color: 'var(--blue-light)', fontSize: 13, cursor: 'pointer' }}
            >
              {sending ? 'Sending...' : 'Resend code'}
            </button>
          )
        )}
      </div>

      <div className="ob-divider">or</div>

      <button className="ob-btn ob-btn-ghost" onClick={onNext}>
        Skip for now — verify later
      </button>

      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', display: 'block', margin: '12px auto 0' }}
      >
        ← Back
      </button>
    </div>
  );
}
